/**
 * Provides a component for the axis management.
 *
 * @packageDocumentation
 * @module AxisManagement
 * @internal
 */

import {
  Box,
  Button,
  Collapse,
  Divider,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { ChevronDown, GripVertical, X } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import {
  DragDropContext,
  Draggable,
  Droppable,
  OnDragEndResponder,
} from 'react-beautiful-dnd';
import { AxisPreferences } from '../../config/plotTypes';
import { getAxisRangeFieldLabels } from './axisRangeLabels';
import { getAxisAvatarLabel } from './axisAvatarLabel';
import { AxisPreferenceSwitch } from './FixedRangeToggle';
import { MinMaxRangeInputs } from './MinMaxRangeInputs';

// -- type definitions --

/**
 * Props for the AxisManagement component.
 */
export interface AxisManagementProps {
  axes: Record<string, AxisPreferences>;
  onUpdateAxis: (key: string, prefs: AxisPreferences) => void;
  onReorderAxis: (sourceIndex: number, destinationIndex: number) => void;
  onAddAxis?: () => void;
  onDeleteAxis?: (key: string) => void;
}

// -- component --

/**
 * The AxisManagement component.
 */
export const AxisManagement: React.FC<AxisManagementProps> = ({
  axes,
  onUpdateAxis,
  onReorderAxis,
  onAddAxis,
  onDeleteAxis,
}) => {
  // -- state from props --

  const axisEntries = Object.entries(axes);
  const nonXAxisCount = axisEntries.filter(
    ([, prefs]) => !prefs.isXAxis
  ).length;

  // -- internal state --

  /**
   * The keys of the axes that are currently expanded.
   */
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(
    new Set(Object.keys(axes))
  );

  // -- internal functions --

  /**
   * Toggle the expansion state of an axis.
   * @param key - The key of the axis to toggle.
   */
  const toggleExpanded = (key: string) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // -- event handlers --

  /**
   * Reorder the axes based on the drag end event.
   * @param result - The result of the drag end event.
   */
  const handleDragEnd: OnDragEndResponder = (result) => {
    if (!result.destination) return;
    onReorderAxis(result.source.index, result.destination.index);
  };

  // -- side effects --

  /**
   * Set the expanded keys when the axes change.
   */
  useEffect(() => {
    setExpandedKeys((prev) => {
      const next = new Set<string>();
      Object.keys(axes).forEach((key) => {
        if (prev.has(key)) {
          next.add(key);
        } else {
          next.add(key);
        }
      });
      return next;
    });
  }, [axes]);

  // -- render --

  return (
    <Box>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        mb={1}
      >
        {/* Title and add button */}
        <Typography
          variant="subtitle2"
          sx={{ fontSize: '0.9rem', fontWeight: 700 }}
        >
          Gestion des axes
        </Typography>
        <Button size="small" variant="text" onClick={() => onAddAxis?.()}>
          + Ajouter
        </Button>
      </Stack>
      {/* Drag-and-drop context for reordering axes */}
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="axes">
          {(provided) => (
            <Box ref={provided.innerRef} {...provided.droppableProps}>
              {/* Render each axis */}
              {axisEntries.map(([key, prefs], index) => {
                const isExpanded = expandedKeys.has(key);
                const zoomEnabled = prefs.zoomEnabled !== false;
                const useBounds = prefs.useBounds === true;
                const rangeLabels = getAxisRangeFieldLabels(zoomEnabled);

                return (
                  <Draggable
                    key={key}
                    draggableId={key}
                    index={index}
                    isDragDisabled={!!prefs.isXAxis}
                  >
                    {(provided) => (
                      <Box
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        sx={{
                          mb: 1,
                          borderRadius: 1,
                          border: '1px solid',
                          borderColor: 'divider',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                          overflow: 'hidden',
                          bgcolor: 'background.paper',
                        }}
                      >
                        {/* Header row: grip | collapse toggle | circle | title */}
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={0.75}
                          px={1}
                          py={0.75}
                        >
                          {!prefs.isXAxis ? (
                            <Box
                              {...provided.dragHandleProps}
                              aria-label="Reorder axis"
                              sx={{
                                cursor: 'grab',
                                color: 'text.disabled',
                                display: 'flex',
                                alignItems: 'center',
                              }}
                            >
                              <GripVertical size={16} />
                            </Box>
                          ) : (
                            /* Hidden spacer with dragHandleProps required by dnd */
                            <Box
                              {...provided.dragHandleProps}
                              sx={{ width: 16, flexShrink: 0 }}
                            />
                          )}
                          {/* Expand toggle; triggers collapse of the axis details */}
                          <IconButton
                            size="small"
                            onClick={() => toggleExpanded(key)}
                            sx={{
                              transition: 'transform 0.2s',
                              transform: isExpanded
                                ? 'rotate(180deg)'
                                : 'rotate(0deg)',
                              flexShrink: 0,
                            }}
                          >
                            <ChevronDown size={16} />
                          </IconButton>
                          {/* Circle with axis key; its "avatar"*/}
                          <Box
                            sx={{
                              width: 24,
                              height: 24,
                              borderRadius: '50%',
                              border: '1.5px solid',
                              borderColor: 'text.secondary',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0,
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                fontSize: 9,
                                fontWeight: 700,
                                lineHeight: 1,
                              }}
                            >
                              {getAxisAvatarLabel(key, prefs)}
                            </Typography>
                          </Box>
                          {/* Axis name */}
                          <Typography
                            variant="body2"
                            noWrap
                            sx={{ flexGrow: 1, minWidth: 0 }}
                          >
                            {prefs.displayName || key}
                          </Typography>
                          {/* Delete button; disabled if the axis is the X axis or the last non-X axis */}
                          <IconButton
                            size="small"
                            onClick={() => onDeleteAxis?.(key)}
                            aria-label="Supprimer l'axe"
                            disabled={
                              !!prefs.isXAxis ||
                              (!prefs.isXAxis && nonXAxisCount <= 1)
                            }
                          >
                            <X size={14} />
                          </IconButton>
                        </Stack>
                        {/* Collapsible axis details section */}
                        <Collapse in={isExpanded}>
                          <Divider />
                          <Box
                            px={1.5}
                            py={1}
                            display="flex"
                            flexDirection="column"
                            gap={1}
                          >
                            {/* Axis name input */}
                            <TextField
                              label="Nom de l'axe"
                              value={prefs.displayName || ''}
                              onChange={(e) =>
                                onUpdateAxis(key, {
                                  ...prefs,
                                  displayName: e.target.value,
                                })
                              }
                              size="small"
                              fullWidth
                              placeholder={key}
                            />
                            {/* Bounds toggle */}
                            <AxisPreferenceSwitch
                              checked={useBounds}
                              label="Définir une plage"
                              onToggle={() =>
                                onUpdateAxis(key, {
                                  ...prefs,
                                  useBounds: !useBounds,
                                })
                              }
                            />
                            {/* Zoom toggle */}
                            <AxisPreferenceSwitch
                              checked={zoomEnabled}
                              label="Zoom autorisé"
                              onToggle={() =>
                                onUpdateAxis(key, {
                                  ...prefs,
                                  zoomEnabled: !zoomEnabled,
                                })
                              }
                            />
                            {/* Min/max range inputs */}
                            <MinMaxRangeInputs
                              min={prefs.min}
                              max={prefs.max}
                              minLabel={rangeLabels.minLabel}
                              maxLabel={rangeLabels.maxLabel}
                              helperText={
                                useBounds ? rangeLabels.helperText : undefined
                              }
                              onChangeMin={(value) =>
                                onUpdateAxis(key, { ...prefs, min: value })
                              }
                              onChangeMax={(value) =>
                                onUpdateAxis(key, { ...prefs, max: value })
                              }
                              disabled={!useBounds}
                            />
                          </Box>
                        </Collapse>
                      </Box>
                    )}
                  </Draggable>
                );
              })}
              {provided.placeholder}
            </Box>
          )}
        </Droppable>
      </DragDropContext>
    </Box>
  );
};
